import { useState } from "react";
import { toast } from "sonner";
import { Archive, FileJson, FileText, Loader2, Mail, RotateCcw, BellRing } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function downloadBlob(data, filename, type) {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function BackupPage() {
  const { user } = useAuth();
  const { t } = useLang();
  const canWrite = ["admin", "editor"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  const [zipLoading, setZipLoading] = useState(false);
  const [jsonLoading, setJsonLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const downloadZip = async () => {
    setZipLoading(true);
    try {
      const { data } = await api.get("/backup/pdf-zip", { responseType: "blob" });
      downloadBlob(data, `temha_yedek_${Date.now()}.zip`, "application/zip");
      toast.success(t("ZIP indirildi"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setZipLoading(false);
    }
  };

  const downloadJson = async () => {
    setJsonLoading(true);
    try {
      const { data } = await api.get("/backup/json", { responseType: "blob" });
      downloadBlob(data, `temha_yedek_${Date.now()}.json`, "application/json");
      toast.success(t("JSON indirildi"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setJsonLoading(false);
    }
  };

  const sendEmail = async () => {
    setEmailLoading(true);
    try {
      const { data } = await api.post("/backup/email");
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEmailLoading(false);
    }
  };

  const doRestore = async () => {
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      const { data } = await api.post("/backup/restore", fd);
      toast.success(data.message);
      setConfirmRestore(false);
      setRestoreFile(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRestoring(false);
    }
  };

  const cards = [
    {
      icon: FileText,
      title: t("PDF Arşivi (ZIP)"),
      desc: t("Tüm modüllerin nizami PDF raporlarını ZIP olarak indirin."),
      action: downloadZip,
      loading: zipLoading,
      label: t("ZIP İndir"),
      testid: "backup-zip-button",
      show: true,
    },
    {
      icon: FileJson,
      title: t("Tam Veri Yedeği (JSON)"),
      desc: t("Tüm kayıtlarınızı geri yükleme formatında indirin."),
      action: downloadJson,
      loading: jsonLoading,
      label: t("JSON İndir"),
      testid: "backup-json-button",
      show: true,
    },
    {
      icon: Mail,
      title: t("E-posta ile Gönder"),
      desc: t("Yedek ZIP'ini indirebileceğiniz güvenli bir bağlantı e-posta adresinize gönderilir (24 saat geçerli)."),
      action: sendEmail,
      loading: emailLoading,
      label: t("Bağlantıyı Gönder"),
      testid: "backup-email-button",
      show: isAdmin,
    },
  ];

  return (
    <div className="fade-up max-w-4xl" data-testid="backup-page">
      <div className="flex items-center gap-3">
        <Archive className="h-7 w-7 text-blue-600" />
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#0A1128]">{t("Yedekleme")}</h1>
      </div>
      <p className="mt-2 text-slate-500">{t("Verilerinizi düzenli olarak yedekleyin.")}</p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
        {cards.filter((c) => c.show).map((c) => (
          <div key={c.testid} className="border border-slate-200 bg-white rounded-md p-6 flex flex-col">
            <div className="h-10 w-10 rounded-md bg-blue-50 flex items-center justify-center">
              <c.icon className="h-5 w-5 text-blue-600" />
            </div>
            <h2 className="font-display text-lg font-semibold text-[#0A1128] mt-4">{c.title}</h2>
            <p className="text-sm text-slate-500 mt-1 flex-1">{c.desc}</p>
            <Button onClick={c.action} disabled={c.loading} data-testid={c.testid}
              className="mt-5 bg-blue-600 hover:bg-blue-700 text-white">
              {c.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : c.label}
            </Button>
          </div>
        ))}
      </div>

      {isAdmin && (
        <div className="mt-6 border border-slate-200 bg-white rounded-md p-6" data-testid="restore-card">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-amber-50 flex items-center justify-center">
              <RotateCcw className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold text-[#0A1128]">{t("Yedekten Geri Yükle")}</h2>
              <p className="text-sm text-slate-500">{t("Daha önce indirdiğiniz JSON yedeğini yükleyin. Mevcut kayıtlarınızın üzerine yazılır.")}</p>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Input type="file" accept=".json" className="max-w-xs focus-visible:ring-blue-600"
              onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
              data-testid="restore-file-input" />
            <Button variant="outline" disabled={!restoreFile}
              onClick={() => setConfirmRestore(true)}
              data-testid="restore-button"
              className="border-amber-300 text-amber-700 hover:bg-amber-50">
              {t("Dosya Seç ve Geri Yükle")}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-6 rounded-md border border-blue-200 bg-blue-50 p-4 flex items-center gap-3" data-testid="weekly-reminder-note">
        <BellRing className="h-5 w-5 text-blue-600 shrink-0" />
        <p className="text-sm text-blue-800">
          <strong>{t("Haftalık hatırlatma")}:</strong>{" "}
          {t("Her hafta yedekleme hatırlatması e-posta adresinize otomatik gönderilir.")}
        </p>
      </div>

      <AlertDialog open={confirmRestore} onOpenChange={setConfirmRestore}>
        <AlertDialogContent data-testid="restore-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Geri yüklemek istediğinize emin misiniz?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("Mevcut tüm kayıtlarınız silinip yedekteki kayıtlarla değiştirilecek. Bu işlem geri alınamaz.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-cancel-button">{t("Vazgeç")}</AlertDialogCancel>
            <AlertDialogAction onClick={doRestore} disabled={restoring}
              data-testid="restore-confirm-button"
              className="bg-amber-600 hover:bg-amber-700 text-white">
              {restoring ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Geri Yükle")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
